function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import { motion, AnimatePresence } from 'framer-motion';
import { mixColors, hexToRgba } from '../../utils/colors';
import { formatBytes } from '../../utils/helpers';
import Alert from '../Alert';
var _borderRadius = function borderRadius(edges, theme) {
  switch (edges) {
    case 'rounded':
      return 99;
    case 'sharp':
      return 0;
    default:
      return theme.shape.borderRadius;
  }
};
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      position: 'relative',
      display: function display(_ref) {
        var compact = _ref.compact;
        return compact ? 'flex' : 'block';
      },
      border: function border(_ref2) {
        var hasErrors = _ref2.hasErrors;
        return "2px dashed " + (hasErrors ? theme.palette.error.main : theme.palette.primary.main);
      },
      padding: function padding(_ref3) {
        var compact = _ref3.compact;
        return compact ? theme.spacing(2, 4) : theme.spacing(6, 4);
      },
      background: function background(_ref4) {
        var disabled = _ref4.disabled;
        return disabled ? theme.palette.grey[300] : null;
      },
      opacity: function opacity(_ref5) {
        var disabled = _ref5.disabled;
        return disabled ? 0.5 : 1;
      },
      borderRadius: function borderRadius(_ref6) {
        var edges = _ref6.edges;
        return _borderRadius(edges, theme);
      },
      '&:focus': {
        border: "2px solid " + theme.palette.primary.main,
        outline: 'none',
        background: hexToRgba(theme.palette.primary.main, 0.1)
      },
      alignItems: 'center',
      '& .MuiTypography-root': {
        marginRight: function marginRight(_ref7) {
          var compact = _ref7.compact;
          return compact ? theme.spacing(2) : 0;
        }
      }
    },
    icon: {
      color: theme.palette.default.light,
      margin: function margin(_ref8) {
        var compact = _ref8.compact;
        return compact ? "0 " + theme.spacing(2) + " 0 0" : '0 auto';
      },
      display: 'block',
      fontSize: function fontSize(_ref9) {
        var compact = _ref9.compact;
        return compact ? '2rem' : '4rem';
      }
    },
    dragging: {
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%',
      opacity: 0.2,
      backgroundColor: function backgroundColor(_ref10) {
        var isDragReject = _ref10.isDragReject;
        return isDragReject ? theme.palette.error.main : theme.palette.primary.main;
      },
      '& .MuiLinearProgress-barColorPrimary': {
        background: function background(_ref11) {
          var isDragReject = _ref11.isDragReject;
          return mixColors(isDragReject ? theme.palette.error.main : theme.palette.primary.main, '#ffffff', 60);
        }
      }
    },
    loading: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -20,
      marginLeft: -20
    }
  };
});
var DropZone = function DropZone(_ref12) {
  var loading = _ref12.loading,
    compact = _ref12.compact,
    disabled = _ref12.disabled,
    onDrop = _ref12.onDrop,
    edges = _ref12.edges,
    dropzoneLabel = _ref12.dropzoneLabel,
    dropzoneDescription = _ref12.dropzoneDescription,
    dropzoneProps = _ref12.dropzoneProps,
    listAcceptedFiles = _ref12.listAcceptedFiles;
  var _useDropzone = useDropzone(_extends({
      onDrop: onDrop,
      disabled: disabled
    }, dropzoneProps)),
    getRootProps = _useDropzone.getRootProps,
    getInputProps = _useDropzone.getInputProps,
    isDragActive = _useDropzone.isDragActive,
    isDragAccept = _useDropzone.isDragAccept,
    isDragReject = _useDropzone.isDragReject,
    acceptedFiles = _useDropzone.acceptedFiles,
    fileRejections = _useDropzone.fileRejections;
  var hasErrors = fileRejections.length > 0;
  var classes = useStyles({
    edges: edges,
    disabled: disabled,
    hasErrors: hasErrors,
    isDragReject: isDragReject,
    compact: compact
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, _extends({
    className: classes.root
  }, getRootProps(), {
    sx: {
      mb: 1
    }
  }), isDragActive && /*#__PURE__*/React.createElement(LinearProgress, {
    className: classes.dragging
  }), loading && !hasErrors && /*#__PURE__*/React.createElement(CircularProgress, {
    className: classes.loading
  }), /*#__PURE__*/React.createElement("input", getInputProps()), isDragAccept && /*#__PURE__*/React.createElement(CheckCircleIcon, {
    className: classes.icon
  }), isDragReject && /*#__PURE__*/React.createElement(WarningIcon, {
    className: classes.icon
  }), !isDragAccept && !isDragReject && /*#__PURE__*/React.createElement(CloudUploadIcon, {
    className: classes.icon
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle2",
    align: "center"
  }, isDragReject ? 'Error' : dropzoneLabel), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    align: "center",
    color: "textSecondary"
  }, isDragReject ? 'The selected file(s) will not be accepted' : dropzoneDescription)), /*#__PURE__*/React.createElement(AnimatePresence, null, listAcceptedFiles && acceptedFiles.length > 0 && /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      translateY: -20,
      opacity: 0
    },
    animate: {
      translateY: 0,
      opacity: 1
    },
    exit: {
      translateY: -20,
      opacity: 0
    },
    transition: {
      duration: 0.25,
      ease: 'easeOut'
    }
  }, acceptedFiles.map(function (file) {
    return /*#__PURE__*/React.createElement(Alert, {
      variant: "standard",
      severity: "success",
      key: file.path,
      style: {
        marginBottom: 5
      }
    }, file.path);
  })), hasErrors && /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      translateY: -20,
      opacity: 0
    },
    animate: {
      translateY: 0,
      opacity: 1
    },
    exit: {
      translateY: -20,
      opacity: 0
    },
    transition: {
      duration: 0.25,
      ease: 'easeOut'
    }
  }, /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    variant: "standard"
  }, fileRejections.map(function (_ref13) {
    var file = _ref13.file,
      errors = _ref13.errors;
    return /*#__PURE__*/React.createElement("div", {
      key: file.path
    }, file.path, /*#__PURE__*/React.createElement("ul", null, errors.map(function (e) {
      if (e.code === 'file-too-large') {
        return /*#__PURE__*/React.createElement("li", {
          key: e.code
        }, "File size is larger than", ' ', formatBytes(dropzoneProps.maxSize));
      }
      return /*#__PURE__*/React.createElement("li", {
        key: e.code
      }, e.message);
    })));
  })))));
};
DropZone.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Whether or not to show a list of accepted files.
   */
  listAcceptedFiles: PropTypes.bool,
  /**
   * Show progress spinner
   */
  loading: PropTypes.bool,
  /**
   * Compact Layout
   */
  compact: PropTypes.bool,
  /**
   * If `true`, the label, input and helper text should be displayed in a disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * Text label displayed in the dropzone
   */
  dropzoneLabel: PropTypes.string,
  /**
   * Text description displayed in the dropzone
   */
  dropzoneDescription: PropTypes.string,
  /**
   * Pass props for react-dropzone. See https://react-dropzone.js.org/
   */
  dropzoneProps: PropTypes.object,
  /**
   * Function: do something when user drops image
   */
  onDrop: PropTypes.func.isRequired,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp'])
} : {};
DropZone.defaultProps = {
  listAcceptedFiles: false,
  disabled: false,
  dropzoneLabel: 'Upload file',
  dropzoneDescription: 'Drag and drop a file here, or click to select a file',
  edges: 'normal',
  loading: false,
  dropzoneProps: null,
  compact: false
};
export default DropZone;